import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trophy, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { engine } from "@/lib/drumAudio";

const PRESETS = [30, 60, 120, 180];

export const PracticeGoal = ({ data, onSaved }) => {
  const [val, setVal] = useState(data.goal ? String(data.goal) : "");
  const thisWeek = data.weeks[data.weeks.length - 1];
  const goal = data.goal;
  const pct = goal ? Math.min(100, (thisWeek.minutes / goal) * 100) : 0;

  useEffect(() => {
    if (!data.goal_reached) return;
    const key = "beatflow_goal_celebrated";
    if (localStorage.getItem(key) === thisWeek.week) return;
    localStorage.setItem(key, thisWeek.week);
    try { engine.celebrate(engine.now + 0.05); } catch (e) { /* noop */ }
  }, [data.goal_reached, thisWeek.week]);

  const save = async (minutes) => {
    const raw = parseInt(minutes, 10) || 0;
    if (raw > 5000) toast.message("That's a lot of drumming! Goal capped at 5000 minutes.");
    const n = Math.max(0, Math.min(5000, raw));
    try {
      await api.put("/practice/goal", { minutes: n });
      setVal(n ? String(n) : "");
      window.dispatchEvent(new CustomEvent("beatflow:goal-changed", { detail: n }));
      toast.success(n ? `Weekly goal set: ${n} minutes` : "Weekly goal cleared");
      onSaved();
    } catch (e) {
      toast.error("Could not save goal");
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="practice-goal">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="font-heading text-sm uppercase tracking-widest text-[#ffab00] flex items-center gap-2">
          <Trophy className="w-4 h-4" /> Weekly goal
        </h4>
        {data.streak > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-tab text-[#ff8a3d]" data-testid="practice-goal-streak">
            <Flame className="w-3.5 h-3.5" /> {data.streak}-week streak
          </span>
        )}
      </div>
      {data.goal_reached && (
        <div className="mb-3 rounded-lg bg-[#ffab00]/15 border border-[#ffab00]/40 px-3 py-2 text-sm text-[#ffab00] font-semibold animate-in zoom-in-95 fade-in duration-300" data-testid="practice-goal-reached">
          Goal smashed! {Math.round(thisWeek.minutes)} of {goal} minutes this week. Great drumming!
        </div>
      )}
      {goal > 0 && (
        <div className="mb-3">
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${data.goal_reached ? "bg-[#ffab00]" : "bg-[#00f0ff]"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[11px] font-tab text-slate-500 mt-1">
            <span data-testid="practice-goal-progress">{Math.round(thisWeek.minutes)} / {goal} min</span>
            <span>Hit {data.weeks_hit} of the last 8 weeks</span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button key={p} onClick={() => save(p)} className={`h-8 px-3 rounded-full text-xs font-tab border transition-colors ${goal === p ? "border-[#ffab00] text-[#ffab00] bg-[#ffab00]/10" : "border-white/15 text-slate-300 hover:border-[#ffab00]/50"}`} data-testid={`practice-goal-preset-${p}`}>
            {p} min
          </button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); save(val); }} className="flex items-center gap-2">
          <Input type="number" min={0} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Custom" className="h-8 w-24 bg-white/[0.03] border-white/10 text-sm" data-testid="practice-goal-input" />
          <Button type="submit" size="sm" className="h-8 bg-[#ffab00] text-black hover:bg-[#ffc233]" data-testid="practice-goal-save">Set</Button>
        </form>
      </div>
    </div>
  );
};
